import React, { useState } from 'react';
import { db } from '../lib/firebase';
import { collection, writeBatch, doc } from 'firebase/firestore';
import { generateUsers, generateVendors, generateListings, generatePosts } from '../utils/mockData';
import { Database, Play, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export const SeedPage = () => {
  const [status, setStatus] = useState('IDLE');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const seedData = async () => {
    setStatus('SEEDING');
    setProgress(0);
    setError(null);

    try {
      // 1. Users (Limit for MVP seed)
      const mockUsers = generateUsers(50);
      const userBatch = writeBatch(db);
      mockUsers.forEach(user => {
        const ref = doc(db, 'users', user.uid);
        userBatch.set(ref, user);
      });
      await userBatch.commit();
      setProgress(25);

      // 2. Vendors
      const mockVendors = generateVendors(mockUsers);
      const vendorBatch = writeBatch(db);
      mockVendors.forEach(vendor => {
        const ref = doc(db, 'vendors', vendor.id);
        vendorBatch.set(ref, vendor);
      });
      await vendorBatch.commit();
      setProgress(50);

      // 3. Listings
      const mockListings = generateListings(mockVendors, 50);
      const listingBatch = writeBatch(db);
      mockListings.forEach(listing => {
        const ref = doc(db, 'listings', listing.id);
        listingBatch.set(ref, listing);
      });
      await listingBatch.commit();
      setProgress(75);

      // 4. Posts
      const mockPosts = generatePosts(mockUsers, 100);
      const postBatch = writeBatch(db);
      mockPosts.forEach(post => {
        const ref = doc(db, 'posts', post.id);
        postBatch.set(ref, post);
      });
      await postBatch.commit();
      setProgress(100);

      setStatus('SUCCESS');
    } catch (err) {
      console.error(err);
      setError(err.message);
      setStatus('ERROR');
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
        <div className="p-8 text-center border-b border-gray-100">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
            <Database size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">MVP Data Seeding</h1>
          <p className="text-gray-500">
            Click the button below to populate your Firestore with realistic mock data 
            (Vendors, Gems, Social Feed, Users).
          </p>
        </div>
        
        <div className="p-8">
          {status === 'IDLE' && (
            <button 
              onClick={seedData}
              className="w-full py-4 bg-primary text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
            >
              <Play size={20} />
              Start Seeding Platform Data
            </button>
          )}

          {status === 'SEEDING' && (
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-4">
                <Loader2 size={40} className="text-primary animate-spin" />
                <p className="font-bold text-gray-900">Seeding Platform Content... {progress}%</p>
              </div>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                <div className="bg-primary h-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}

          {status === 'SUCCESS' && (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 size={24} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Platform Seeded Successfully!</h2>
              <p className="text-gray-500">All vendors, gems, and posts are now live in your database.</p>
              <button 
                onClick={() => window.location.href = '/'}
                className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold mt-4"
              >
                Go to Home Feed
              </button>
            </div>
          )}

          {status === 'ERROR' && (
            <div className="bg-red-50 p-6 rounded-xl border border-red-100 flex gap-4">
              <AlertCircle className="text-red-600 shrink-0" size={24} />
              <div>
                <h3 className="font-bold text-red-900 mb-1">Seeding Failed</h3>
                <p className="text-sm text-red-700">{error}</p>
                <button 
                  onClick={seedData}
                  className="mt-4 text-sm font-bold text-red-600 hover:underline"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
